import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { fmtINR, compactIndian, fmtPct } from "@/lib/format";
import {
  Wallet, TrendingUp, Globe2, Layers, Briefcase, ArrowUpRight, ArrowDownRight,
  ShieldAlert, CalendarClock,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";

function money(v) { return "₹" + compactIndian(v); }

// Dark-friendly chart tokens
const C = {
  axis: "rgba(207,199,186,0.5)",
  grid: "rgba(231,215,191,0.07)",
  tooltipBg: "rgba(16,27,43,0.96)",
  tooltipBorder: "rgba(231,215,191,0.18)",
  cursor: "rgba(176,141,87,0.10)",
  brass: "#B08D57",
  sand: "#D7C09A",
  navyTint: "#8AA6D6",
  emeraldTint: "#74C2A3",
};
const DARK_PALETTE = ["#B08D57", "#74C2A3", "#8AA6D6", "#C58B7A", "#D7C09A", "#C9A24B", "#A98BC9", "#5FB0A6", "#7FA9C9", "#D0A05A"];

function ChartCard({ title, right, children, className, testid }) {
  return (
    <Card
      className={cn(
        "border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-5 shadow-[inset_0_1px_0_rgba(231,215,191,0.04),0_12px_30px_rgba(0,0,0,0.35)]",
        className
      )}
      data-testid={testid}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[color:var(--mk-text)]">{title}</h3>
        {right}
      </div>
      {children}
    </Card>
  );
}

function cn(...a) { return a.filter(Boolean).join(" "); }

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-[0_12px_30px_rgba(0,0,0,0.5)]"
      style={{ background: C.tooltipBg, border: `1px solid ${C.tooltipBorder}` }}
    >
      {label && <div className="mb-1 font-medium text-[color:var(--mk-text)]">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-[color:var(--mk-text-2)]">{p.name}:</span>
          <span className="font-data font-medium tabular-nums text-[color:var(--mk-text)]">{fmtINR(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div>
        <PageHeader title="Overview" subtitle="Loading your consolidated wealth…" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl bg-[color:var(--mk-surface-2)]" />)}
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-12">
          <Skeleton className="h-80 rounded-2xl bg-[color:var(--mk-surface-2)] lg:col-span-8" />
          <Skeleton className="h-80 rounded-2xl bg-[color:var(--mk-surface-2)] lg:col-span-4" />
        </div>
      </div>
    );
  }

  const empty = !data || data.holdings_count === 0;
  const gainPositive = (data?.total_gain_inr || 0) >= 0;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Overview" subtitle="Consolidated net worth across all entities, assets and currencies">
        <Button
          onClick={() => navigate("/holdings")}
          className="bg-[var(--primary-deep-green)] text-white hover:bg-[var(--primary-deep-green-hover)]"
          data-testid="dashboard-add-holding"
        >
          <Briefcase className="mr-2 h-4 w-4" /> Manage Portfolio
        </Button>
      </PageHeader>

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard testid="dashboard-net-worth-total" label="Total Net Worth" icon={Wallet} accent="#B08D57"
          value={<span data-testid="kpi-net-worth-value">{money(data.total_net_worth_inr)}</span>}
          sub={<span className="font-data">{fmtINR(data.total_net_worth_inr)}</span>} />
        <StatCard testid="dashboard-gain" label="Unrealised Gain" icon={gainPositive ? TrendingUp : ArrowDownRight} accent={gainPositive ? "#74C2A3" : "#D98A8A"}
          value={<span className={gainPositive ? "text-[#74C2A3]" : "text-[#D98A8A]"}>{money(data.total_gain_inr)}</span>}
          sub={<span className="inline-flex items-center gap-1">{gainPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{fmtPct(data.gain_pct)} vs cost</span>} />
        <StatCard testid="dashboard-foreign" label="Foreign Exposure" icon={Globe2} accent="#8AA6D6"
          value={money(data.foreign_exposure_inr)}
          sub={`${data.foreign_pct.toFixed(1)}% of portfolio`} />
        <StatCard testid="dashboard-holdings-count" label="Holdings" icon={Layers} accent="#D7C09A"
          value={data.holdings_count}
          sub={`${data.allocation_by_class.length} asset classes`} />
      </div>

      {empty ? (
        <Card className="mt-6 border-[color:var(--mk-border)] bg-[color:var(--mk-surface)]">
          <EmptyState icon={Briefcase} title="No holdings yet"
            description="Start building your consolidated view by adding holdings manually or importing a CSV / Excel file."
            action={<Button onClick={() => navigate("/holdings")} className="bg-[var(--primary-deep-green)] text-white hover:bg-[var(--primary-deep-green-hover)]" data-testid="empty-add-holding">Go to Portfolio</Button>} />
        </Card>
      ) : (
        <>
          <div className="mt-4 grid gap-4 lg:grid-cols-12">
            <ChartCard title="Net Worth Trend" className="lg:col-span-8" testid="dashboard-net-worth-trend"
              right={<Badge className="border-[color:var(--mk-border-strong)] bg-[color:var(--mk-primary)]/12 text-[11px] text-[color:var(--mk-primary-2)]">INR · since inception</Badge>}>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trend} margin={{ left: 8, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="nw" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.brass} stopOpacity={0.22} />
                        <stop offset="100%" stopColor={C.brass} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: C.axis, fontSize: 11 }} stroke={C.grid} tickFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} />
                    <YAxis tick={{ fill: C.axis, fontSize: 11 }} stroke={C.grid} tickFormatter={(v) => compactIndian(v)} width={52} />
                    <RTooltip content={<CustomTooltip />} cursor={{ stroke: C.cursor, strokeWidth: 24 }} />
                    <Line type="monotone" dataKey="value" name="Net Worth" stroke={C.brass} strokeWidth={2.25} dot={false} activeDot={{ r: 4, fill: C.brass }} fill="url(#nw)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Asset Allocation" className="lg:col-span-4">
              <div className="relative h-72" data-testid="dashboard-allocation-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.allocation_by_class} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={64} outerRadius={96} paddingAngle={2} stroke="none">
                      {data.allocation_by_class.map((e, i) => (
                        <Cell key={i} fill={DARK_PALETTE[i % DARK_PALETTE.length]} />
                      ))}
                    </Pie>
                    <RTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[11px] text-[color:var(--mk-muted)]">Total</span>
                  <span className="font-data text-lg font-semibold tabular-nums text-[color:var(--mk-text)]">{money(data.total_net_worth_inr)}</span>
                </div>
              </div>
              <div className="mt-3 max-h-28 space-y-1.5 overflow-y-auto">
                {data.allocation_by_class.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-[color:var(--mk-text-2)]">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: DARK_PALETTE[i % DARK_PALETTE.length] }} />{e.name}
                    </span>
                    <span className="font-data tabular-nums text-[color:var(--mk-text)]">{((e.value / data.total_net_worth_inr) * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-12">
            <ChartCard title="Allocation by Entity" className="lg:col-span-5">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.allocation_by_entity} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                    <XAxis type="number" tick={{ fill: C.axis, fontSize: 11 }} stroke={C.grid} tickFormatter={(v) => compactIndian(v)} />
                    <YAxis type="category" dataKey="name" tick={{ fill: C.axis, fontSize: 11 }} stroke={C.grid} width={110} />
                    <RTooltip content={<CustomTooltip />} cursor={{ fill: C.cursor }} />
                    <Bar dataKey="value" name="Value" radius={[0, 6, 6, 0]} fill={C.navyTint} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Currency Mix" className="lg:col-span-3">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.allocation_by_currency} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} stroke="#070A0F" strokeWidth={2}>
                      {data.allocation_by_currency.map((e, i) => <Cell key={i} fill={DARK_PALETTE[i % DARK_PALETTE.length]} />)}
                    </Pie>
                    <RTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {data.allocation_by_currency.map((e, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-[color:var(--mk-text-2)]">
                    <span className="h-2 w-2 rounded-full" style={{ background: DARK_PALETTE[i % DARK_PALETTE.length] }} />{e.name}
                  </span>
                ))}
              </div>
            </ChartCard>

            <Card className="border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-5 lg:col-span-4">
              <h3 className="mb-4 text-sm font-semibold text-[color:var(--mk-text)]">Compliance Snapshot</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-3">
                  <span className="flex items-center gap-2 text-sm text-[color:var(--mk-text-2)]"><CalendarClock className="h-4 w-4 text-[color:var(--mk-primary)]" /> Upcoming tasks</span>
                  <span className="font-data text-lg font-semibold tabular-nums text-[color:var(--mk-text)]">{data.tasks_upcoming}</span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-[#4A1F2A]/55 bg-[#4A1F2A]/35 p-3">
                  <span className="flex items-center gap-2 text-sm text-[#E2B4B4]"><ShieldAlert className="h-4 w-4" /> Overdue</span>
                  <span className="font-data text-lg font-semibold tabular-nums text-[#E2B4B4]">{data.tasks_overdue}</span>
                </div>
                <Button variant="outline" className="w-full border-[color:var(--mk-border-strong)] bg-transparent text-[color:var(--mk-text)] hover:bg-white/5" onClick={() => navigate("/compliance")} data-testid="dashboard-view-compliance">View Compliance</Button>
              </div>
            </Card>
          </div>

          {/* Top holdings */}
          <Card className="mt-4 overflow-hidden border-[color:var(--mk-border)] bg-[color:var(--mk-surface)]">
            <div className="flex items-center justify-between p-5 pb-3">
              <h3 className="text-sm font-semibold text-[color:var(--mk-text)]">Top Holdings</h3>
              <Button variant="ghost" size="sm" className="text-[color:var(--mk-text-2)] hover:bg-white/5 hover:text-[color:var(--mk-text)]" onClick={() => navigate("/holdings")}>View all</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dashboard-top-holdings-table">
                <thead className="bg-[color:var(--mk-surface-2)]">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--mk-text-2)]">
                    <th className="px-5 py-3">Instrument</th>
                    <th className="px-5 py-3">Asset Class</th>
                    <th className="px-5 py-3">Entity</th>
                    <th className="px-5 py-3 text-right">Value (INR)</th>
                    <th className="px-5 py-3 text-right">Gain</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_holdings.map((h, i) => (
                    <tr key={i} className="border-t border-[color:var(--mk-border)] transition-colors hover:bg-[rgba(176,141,87,0.06)]">
                      <td className="px-5 py-3 font-medium text-[color:var(--mk-text)]">{h.name}</td>
                      <td className="px-5 py-3"><span className="inline-flex items-center gap-1.5 text-xs text-[color:var(--mk-text-2)]"><span className="h-2 w-2 rounded-full" style={{ background: DARK_PALETTE[i % DARK_PALETTE.length] }} />{h.asset_class}</span></td>
                      <td className="px-5 py-3 text-[color:var(--mk-text-2)]">{h.entity_name}</td>
                      <td className="px-5 py-3 text-right font-data tabular-nums text-[color:var(--mk-text)]">{fmtINR(h.market_value_inr)}</td>
                      <td className={"px-5 py-3 text-right font-data tabular-nums " + (h.gain_inr >= 0 ? "text-[#74C2A3]" : "text-[#D98A8A]")}>{money(h.gain_inr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
